import { nextTick, onBeforeUnmount, ref, watch } from 'vue';

/**
 * Keyboard navigation + active-option tracking for the dropdown.
 * Owns `activeIndex` and the typeahead buffer. Returns the per-key handlers
 * the template binds to and a couple of helpers (initialActiveIndex,
 * scrollActiveIntoView) that the host component needs when opening the panel.
 *
 * @param {object} ctx
 * @param {import('vue').Ref<boolean>} ctx.open
 * @param {() => void} ctx.openPanel
 * @param {() => void} ctx.close
 * @param {() => void} ctx.toggle
 * @param {import('vue').ComputedRef<boolean>} ctx.searchable
 * @param {import('vue').ComputedRef<Array<{value:string,label:string,disabled:boolean}>>} ctx.filteredOptions
 * @param {import('vue').Ref<HTMLElement|null>} ctx.listRef
 * @param {import('vue').Ref<string[]>} ctx.selectedValues
 * @param {import('vue').Ref<string>} ctx.search
 * @param {(opt: object) => void} ctx.pick
 */
export function useKeyboardNav({
    open,
    openPanel,
    close,
    toggle,
    searchable,
    filteredOptions,
    listRef,
    selectedValues,
    search,
    pick,
}) {
    const activeIndex = ref(-1);

    function initialActiveIndex() {
        const list = filteredOptions.value;
        if (!list.length) return -1;
        const cur = selectedValues.value[0];
        const idx = list.findIndex((o) => o.value === cur && !o.disabled);
        if (idx >= 0) return idx;
        return list.findIndex((o) => !o.disabled);
    }

    function scrollActiveIntoView() {
        nextTick(() => {
            if (!listRef.value || activeIndex.value < 0) return;
            const items = listRef.value.querySelectorAll('.lfm-select__option');
            const el = items[activeIndex.value];
            if (el && typeof el.scrollIntoView === 'function') {
                el.scrollIntoView({ block: 'nearest' });
            }
        });
    }

    function setActive(idx) {
        const list = filteredOptions.value;
        if (idx < 0 || idx >= list.length) return;
        if (list[idx].disabled) return;
        activeIndex.value = idx;
        scrollActiveIntoView();
    }

    function moveActive(delta) {
        const list = filteredOptions.value;
        if (!list.length) return;
        let i = activeIndex.value;
        for (let step = 0; step < list.length; step++) {
            i = (i + delta + list.length) % list.length;
            if (!list[i].disabled) {
                activeIndex.value = i;
                scrollActiveIntoView();
                return;
            }
        }
    }

    function onArrowKey(delta) {
        if (!open.value) {
            openPanel();
            return;
        }
        moveActive(delta);
    }

    function onHomeEnd(which) {
        if (!open.value) {
            openPanel();
            return;
        }
        const list = filteredOptions.value;
        if (!list.length) return;
        if (which === 'home') {
            const idx = list.findIndex((o) => !o.disabled);
            if (idx >= 0) setActive(idx);
        } else {
            for (let i = list.length - 1; i >= 0; i--) {
                if (!list[i].disabled) {
                    setActive(i);
                    break;
                }
            }
        }
    }

    function onEnter() {
        if (!open.value) {
            openPanel();
            return;
        }
        if (activeIndex.value < 0) return;
        const opt = filteredOptions.value[activeIndex.value];
        if (opt) pick(opt);
    }

    function onSpace() {
        if (searchable.value && open.value) return;
        toggle();
    }

    function onEscape() {
        if (open.value) close();
    }

    let typeaheadBuffer = '';
    let typeaheadTimer = null;

    function onTypeahead(event) {
        if (searchable.value) return;
        if (event.key.length !== 1 || event.ctrlKey || event.altKey || event.metaKey) {
            return;
        }
        if (!open.value) openPanel();

        typeaheadBuffer += event.key.toLowerCase();
        clearTimeout(typeaheadTimer);
        typeaheadTimer = setTimeout(() => {
            typeaheadBuffer = '';
        }, 500);

        const list = filteredOptions.value;
        const idx = list.findIndex(
            (o) => !o.disabled && o.label.toLowerCase().startsWith(typeaheadBuffer),
        );
        if (idx >= 0) setActive(idx);
    }

    watch(
        () => filteredOptions.value.length,
        () => {
            if (!open.value) return;
            if (activeIndex.value >= filteredOptions.value.length) {
                activeIndex.value = filteredOptions.value.length - 1;
            }
        },
    );

    watch(search, () => {
        if (!open.value) return;
        activeIndex.value = initialActiveIndex();
    });

    onBeforeUnmount(() => {
        clearTimeout(typeaheadTimer);
    });

    return {
        activeIndex,
        initialActiveIndex,
        scrollActiveIntoView,
        onArrowKey,
        onHomeEnd,
        onEnter,
        onSpace,
        onEscape,
        onTypeahead,
    };
}
